import { Calendar, Eye, FileText, Mail, MessageCircle, User } from "lucide-react";

import { Badge } from "@/components/ui/badge/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import type { SharedRecord } from "@/features/imagings/schemas/imagings";
import { formatDateTime } from "@/shared/utils/date";

interface SharedRecordDetailProps {
  isOpen: boolean;
  onClose: () => void;
  record: SharedRecord | null;
}

/**
 * Componente para mostrar los detalles de un registro compartido
 */
export function SharedRecordDetail({ isOpen, onClose, record }: SharedRecordDetailProps) {
  if (!record) return null;

  const getStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case "viewed":
        return "default";
      case "active":
        return "secondary";
      case "expired":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case "viewed":
        return "Visto";
      case "active":
        return "Activo";
      case "expired":
        return "Expirado";
      default:
        return "Desconocido";
    }
  };

  const getObjectTypeText = (objectType?: string) => {
    switch (objectType) {
      case "imaging_access":
        return "Estudio de Imagenología";
      case "report_access":
        return "Informe Médico";
      default:
        return "Desconocido";
    }
  };

  const getTypeText = (type?: string) => {
    switch (type) {
      case "email":
        return "Email";
      case "ws":
        return "WhatsApp";
      default:
        return "Desconocido";
    }
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case "email":
        return <Mail className="h-4 w-4" />;
      case "ws":
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {record.object_shared === "imaging_access" ? (
              <Eye className="h-5 w-5 text-blue-600" />
            ) : (
              <FileText className="h-5 w-5 text-green-600" />
            )}
            Detalles del Compartido
          </DialogTitle>
          <DialogDescription>
            Información detallada sobre este registro compartido
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tipo de objeto compartido */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">Tipo:</span>
            </div>
            <Badge variant="outline">{getObjectTypeText(record.object_shared)}</Badge>
          </div>

          <Separator />

          {/* Destinatario */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <span className="font-medium">Destinatario:</span>
            </div>
            <div className="pl-6">
              <p className="text-sm">
                {record.user_receiving.includes("@")
                  ? record.user_receiving
                  : record.phone}
              </p>
              <p className="text-xs text-gray-500">
                {record.user_receiving.includes("@") ? "Email" : "Teléfono"}
              </p>
            </div>
          </div>

          <Separator />

          {/* Remitente */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <span className="font-medium">Compartido por:</span>
            </div>
            <div className="pl-6">
              <p className="text-sm">{record.user_sharing}</p>
            </div>
          </div>

          <Separator />

          {/* Método de envío */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getTypeIcon(record.type)}
              <span className="font-medium">Método:</span>
            </div>
            <Badge variant="secondary">{getTypeText(record.type)}</Badge>
          </div>

          <Separator />

          {/* Fecha de compartido */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="font-medium">Fecha de compartido:</span>
            </div>
            <div className="pl-6">
              <p className="text-sm">{formatDateTime(record.created_at)}</p>
            </div>
          </div>

          <Separator />

          {/* Estado */}
          <div className="flex items-center justify-between">
            <span className="font-medium">Estado:</span>
            <Badge variant={getStatusBadgeVariant(record.status)}>
              {getStatusText(record.status)}
            </Badge>
          </div>

          {/* Información adicional */}
          {record.manual && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="font-medium">Tipo de informe:</span>
                <Badge variant="secondary">Manual</Badge>
              </div>
            </>
          )}

          {/* ID de tracking */}
          <Separator />
          <div className="space-y-2">
            <span className="font-medium">ID de Tracking:</span>
            <div className="pl-4">
              <p className="text-xs font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 p-2 rounded">
                {record.id}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
